// Adapted from Dealtracker src/appframe/tours/deepLink.tsx (commit 30ca0cb) to lib/router.ts. Since
// userhub plan G7 the link is `/tour/<n>` (singular) and opens the userhub catalog, not the small
// window; it still never starts the tour.
import { useEffect, useRef } from 'react'
import { TOUR_NUMBER, homePath, parseRoute, replaceUrl, usePath } from '../lib/router'

/** `?tour=<n>`: on any path, opens the catalog on tour #n over that path's view. */
export const TOUR_PARAM = 'tour'

/** The tour a URL links to — `/tour/<n>` or `?tour=<n>` — or null. A malformed `/tour/x` is 0. */
export function tourLinkNumber(pathname: string, search: string): number | null {
  const fromPath = parseRoute(pathname).tour
  if (fromPath !== undefined) return fromPath
  const raw = new URLSearchParams(search).get(TOUR_PARAM)
  return raw !== null && TOUR_NUMBER.test(raw) ? Number(raw) : null
}

/** The same URL without its tour link: `/tour/<n>` becomes the home page, `?tour` is dropped. */
export function withoutTourLink(url: URL): string {
  const path = parseRoute(url.pathname).tour !== undefined ? homePath : url.pathname
  const params = new URLSearchParams(url.search)
  params.delete(TOUR_PARAM)
  const rest = params.toString()
  return path + (rest ? `?${rest}` : '') + url.hash
}

/**
 * Follows a tour link: calls `onOpen(n)` and drops the link from the URL (replace), so a reload or
 * a copied address bar does not reopen the catalog. Runs on every path change, so a `niobe://tour/<n>`
 * link that Desktop hands over through onDesktopNavigate (a navigate() to `/tour/<n>`) is followed too.
 */
export function useTourLink(onOpen: (n: number) => void): void {
  const path = usePath()
  const onOpenRef = useRef(onOpen)
  onOpenRef.current = onOpen
  useEffect(() => {
    const n = tourLinkNumber(window.location.pathname, window.location.search)
    if (n === null) return
    onOpenRef.current(n)
    replaceUrl(withoutTourLink(new URL(window.location.href)))
  }, [path])
}

/**
 * A tour link must survive sign-in. `loginRedirect` returns to the app origin and drops the path
 * and the query (like the userhub links, userhub/deep-link.ts), so AuthGate stashes the number
 * before signing in and puts it back as `?tour=<n>` just before the signed-in shell mounts.
 */
const KEY = 'niobe.tour.link'

export function stashTourLink(): void {
  const n = tourLinkNumber(window.location.pathname, window.location.search)
  if (n === null) return
  try {
    sessionStorage.setItem(KEY, String(n))
  } catch {
    /* storage unavailable — the link then just opens the app */
  }
}

export function restoreTourLink(): void {
  let stashed: string | null = null
  try {
    stashed = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
  } catch {
    return
  }
  if (stashed === null || !TOUR_NUMBER.test(stashed)) return
  if (tourLinkNumber(window.location.pathname, window.location.search) !== null) return
  const url = new URL(window.location.href)
  url.searchParams.set(TOUR_PARAM, stashed)
  window.history.replaceState(window.history.state, '', url.toString())
}
